import { z } from 'zod';
import { PASSWORD_MIN } from './signUp.js';
/**
 * The platform's own accounts, on the wire.
 *
 * A separate module from `team.ts`, which is about members of a customer's
 * organisation. The two look alike and must never be confused: one is a
 * customer's colleague, the other can read every customer's data.
 *
 * **There is no sign-up schema here, and that is deliberate.** Nothing in this
 * file describes a request that creates an account without an existing
 * operator behind it. The first one is made by a command on the server.
 */
/**
 * The same floor the tenant application uses.
 *
 * It was sixteen, on the reasoning that the blast radius here is the whole
 * platform rather than one organisation. Lowered to match the tenant minimum
 * on 20 Oct 2026, deliberately — see `PASSWORD_MIN`, which records the same
 * decision and what still stands behind it.
 *
 * Imported rather than redeclared, so the two cannot drift: the day somebody
 * raises one, the other moves with it.
 */
export const OPERATOR_PASSWORD_MIN = PASSWORD_MIN;
export const operatorSignInSchema = z.object({
    email: z.string().trim().email().max(254),
    /** Not length-checked on the way in: the answer for a wrong password is the
     *  same whatever its shape, and a minimum here would tell somebody their
     *  guess was too short rather than wrong. */
    password: z.string().min(1).max(512),
    /**
     * Keep this browser signed in for thirty days instead of twelve hours.
     *
     * Optional and absent by default: a session that reaches every organisation
     * on the platform should be short unless somebody deliberately says
     * otherwise, and an absent field must mean the shorter one.
     */
    remember: z.boolean().optional(),
});
export const newOperatorSchema = z.object({
    email: z.string().trim().email().max(254),
    name: z.string().trim().max(120).default(''),
    password: z.string().min(OPERATOR_PASSWORD_MIN).max(512),
    role: z.enum(['owner', 'admin', 'member']),
});
export const operatorSchema = z.object({
    operatorId: z.string(),
    email: z.string(),
    name: z.string(),
    role: z.string(),
    /** Set while suspended. A suspended operator keeps their history. */
    disabledAt: z.string().datetime().nullable(),
    lastSeenAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /** Who added them, by address. Null for the first operator. */
    createdBy: z.string().nullable(),
});
export const operatorListSchema = z.object({ operators: z.array(operatorSchema) });
/** `operator` is null only when a session resolved to an account that vanished. */
export const operatorSessionSchema = z.object({ operator: operatorSchema.nullable() });
export const createOperatorResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('created'), operatorId: z.string() }),
    z.object({ status: z.literal('email-taken') }),
]);
/**
 * Changing your own password.
 *
 * The current one is required, and that is not a formality: without it an
 * unattended signed-in browser is a permanent account takeover rather than a
 * session somebody can end. The new one meets the same minimum a new operator
 * does — the account is no less powerful for having existed a while.
 */
export const changeOperatorPasswordSchema = z.object({
    currentPassword: z.string().min(1).max(512),
    newPassword: z.string().min(OPERATOR_PASSWORD_MIN).max(512),
});
//# sourceMappingURL=operators.js.map